import { doc, getDoc } from "firebase/firestore";

import { db } from "@/integrations/firebase/firestore";
import { normalizeQueryKey } from "@/routes/library-alias";
import { canReuseCompletedSearch } from "@/routes/report-source";

// Client-readable library membership: the alias map (query_key -> drug_id)
// plus `drugs/{id}.latest_version`. Mirrors the server's own serve order, so a
// hit here is exactly what the backend would answer from cache.

export type LibraryStatus = "ready" | "new";

async function readAliasDrugId(query: string): Promise<string | null> {
  const key = normalizeQueryKey(query);
  if (!key || key === "unknown") return null;
  const snap = await getDoc(doc(db, "drug_aliases", key));
  const drugId = snap.data()?.drug_id;
  return typeof drugId === "string" && drugId.length > 0 ? drugId : null;
}

async function readHasLibraryReport(drugId: string): Promise<boolean> {
  const snap = await getDoc(doc(db, "drugs", drugId));
  const latest = snap.data()?.latest_version as number | undefined;
  return canReuseCompletedSearch(latest);
}

// Any miss or failure (offline, rules not yet deployed) is null: the backend
// path, which also resolves synonyms and brands, takes over.
export async function lookupLibraryDrugId(
  query: string,
): Promise<string | null> {
  try {
    return await readAliasDrugId(query);
  } catch (error) {
    console.error("Alias fast-path lookup failed:", error);
    return null;
  }
}

// An unreadable read counts as "no": a needless regeneration is cheap, whereas
// reattaching to a dead pointer strands the doctor.
export async function hasLibraryReport(drugId?: string): Promise<boolean> {
  if (!drugId) return false;
  try {
    return await readHasLibraryReport(drugId);
  } catch (error) {
    console.error("Library presence check failed:", error);
    return false;
  }
}

// Throws on a failed read, so a caller can show "unknown" instead of a false
// "new report" promise.
export async function resolveLibraryStatus(
  name: string,
): Promise<LibraryStatus> {
  const drugId = await readAliasDrugId(name);
  if (!drugId) return "new";
  return (await readHasLibraryReport(drugId)) ? "ready" : "new";
}
